// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { PanelMenuPresentationProps } from "@ces/MobiusOverrides/PanelMenu";
import { constants, DivWrapper } from "@ces/XDL/constants";
import BackArrowLeft from "@ces/XDL/Icons/BackArrowLeft";
import LinkArrowRight from "@ces/XDL/Icons/LinkArrowRight";
import { fontConfig } from "@ces/XDL/PreStyle/typography";
import xBreakpointMediaQueries from "@ces/XDL/Utilities/breakpointMediaQueries";
import color, { getSystemColor } from "@ces/XDL/Utilities/color";
import { iconButtonCommonStyles } from "@ces/XDL/Utilities/links";
import { mergeWithCss } from "@ces/XDL/Utilities/mergeWithCss";
import { styledScrollbar } from "@ces/XDL/Utilities/scroll";
import xScreen from "@ces/XDL/Utilities/xScreen";
import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { StyledButton } from "@insite/mobius/Clickable";
import baseTheme from "@insite/mobius/globals/baseTheme";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import { css } from "styled-components";

export const brandRowSeparatorSelector = "> *:first-child:not(:only-child)::after";

export const hideElement = css`
    &&&& {
        display: none;
        @media print {
            display: none;
        }
    }
`;

export const hideElementOnPrint = css`
    &&&& {
        @media print {
            display: none;
        }
    }
`;

export const displayFlexOnPrint = css`
    &&&& {
        @media print {
            display: flex;
            margin-right: 0;
        }
    }
`;

export const avoidBreakInsidePrintElement = css`
    @media print {
        -webkit-column-break-inside: avoid;
        page-break-inside: avoid;
        break-inside: avoid;
    }
`;

export const hideInputNumberArrows = css`
    // Hide Arrows From Input Number
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    /* Firefox */
    &[type="number"] {
        -moz-appearance: textfield;
    }
`;

export interface ModalButtonsActionStyles {
    buttonsGridItem?: GridItemProps;
    buttonsWrapper?: InjectableCss;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
}

export const layoutContainer: any = css`
    @media print {
        max-width: 100% !important;
        margin: 0 !important;
    }

    ${({ theme }: any) => {
        const { maxWidths }: { maxWidths: number[] } = theme.breakpoints || baseTheme.breakpoints;
        const { paddingValues = [20] }: { paddingValues: number[] } = theme.breakpoints;
        const containerStyles = maxWidths.map(
            (maxWidth, index) =>
                css`
                    &:not(.full-width) {
                        max-width: ${maxWidth}px;
                        padding-left: ${paddingValues[index] || paddingValues[0]}px;
                        padding-right: ${paddingValues[index] || paddingValues[0]}px;
                        margin-right: auto;
                        margin-left: auto;
                    }
                    &.mobile-full-width,
                    &.full-width {
                        max-width: 100%;
                        margin-right: 0;
                        margin-left: 0;
                        padding-left: 0;
                        padding-right: 0;
                    }
                `,
        );
        return css`
            ${xBreakpointMediaQueries(theme, containerStyles)}
        `;
    }}
`;

export const companyLogoItem: any = {
    css: css`
        display: none;
        padding: 0;
        width: 175px;
        height: 75px;

        img {
            display: block;
            width: 175px;
            height: 75px;
        }

        @media print {
            display: block;
            padding-bottom: 20px;
            padding-left: 25px;
            margin-top: 5px;
            float: left;
        }
    `,
};

export const selectedPanelItem = css`
    background: ${getSystemColor("primary")};
    & > ${TypographyStyle}, & > ${IconWrapper} {
        color: ${getSystemColor("common.ui1")};
        background: transparent;
    }
`;

export const modalButtonsActionStyles: ModalButtonsActionStyles = {
    buttonsGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: flex-end;
            flex-wrap: wrap;
        `,
    },
    cancelButton: {
        variant: "secondary",
        css: css`
            margin-right: 10px;
            ${xScreen("lte", "xs")} {
                width: 100%;
                margin-right: 0;
                margin-top: 10px;
                order: 1;
            }
        `,
    },
    submitButton: {
        variant: "primary",
        css: css`
            ${xScreen("lte", "xs")} {
                width: 100%;
                order: 0;
            }
        `,
    },
};

export const sharedPanelMenuStyles: PanelMenuPresentationProps = {
    mergeCss: true,
    backIconProps: {
        src: BackArrowLeft,
        size: 13,
        color: "text.link",
        css: css`
            opacity: 0.66;
            margin-right: 8px;
        `,
    },
    panelChildrenSeparator: {
        css: css`
            margin: 16px auto 10px;
            border-top: 1px solid ${color("common.border")};
            width: calc(100% - 32px);
        `,
    },
    cssOverrides: {
        children: css`
            background-color: ${color("common.white")};
            & > button {
                padding: 0px;
                font-size: 14px;
                line-height: 22px;
                font-weight: ${fontConfig.weight.regular};
                min-height: 22px;
                color: ${color("common.black")};
                ${IconWrapper} {
                    color: ${color("text.link")};
                }
            }
        `,
        wrapper: css`
            justify-content: space-between;
            & > ${IconWrapper} {
                padding-right: 8px;
                height: 22px;
                width: 13px;
                margin-right: 10px;
                color: ${color("text.link")};
            }

            ${xScreen("lte", "md")} {
                &:focus:not(:disabled),
                &:focus:not(:disabled) > ${StyledButton} {
                    // if it has selected item then consider it as selected
                    ${selectedPanelItem}
                }
            }
        `,
        menu: css`
            display: flex;
            flex-direction: column;
            row-gap: 4px;
            ${styledScrollbar()}
        `,
    },
    panelRowProps: {
        css: css`
            padding: 6px 16px 10px;
            border-radius: 0;
            margin: 0;
            text-align: left;
            ${IconWrapper} {
                color: ${color("text.link")};
                width: auto;
                height: 100%;
            }
            ${TypographyStyle} {
                font-size: 14px;
                line-height: 22px;
                font-weight: ${fontConfig.weight.bold};
            }
        `,
        isCurrentCss: css`
            ${selectedPanelItem}
        `,
    },
    backLink: {
        mergeCss: true,
        icon: {
            iconProps: {
                src: BackArrowLeft,
                size: 13,
            },
        },
        typographyProps: {
            size: 12,
            lineHeight: "13px",
            weight: fontConfig.weight.bold,
        },
    },
    parentLinkButton: {
        icon: {
            position: "right",
            iconProps: {
                src: LinkArrowRight,
                size: 14,
            },
        },
        typographyProps: {
            size: 14,
            lineHeight: "22px",
            weight: fontConfig.weight.bold,
        },
        css: css`
            width: fit-content;
            padding: 0 16px;
            margin-bottom: 8px;
        `,
    },
};

export const generateButtonLinkStyle = (iconSrc: React.ComponentType | string) => {
    return {
        ...mergeWithCss(
            iconButtonCommonStyles(iconSrc, 12, {
                typographyProps: {
                    size: 12,
                    weight: "bold",
                    css: css`
                        line-height: 12px;
                    `,
                },
            }),
            {
                css: css`
                    margin: 0 10px;
                    display: flex;
                `,
            },
        ),
    };
};

export const createLinkIconStyles = ({
    icon,
    linkStyles = {},
    typographyStyles = {},
    iconStyles = {},
    iconPosition = "left",
}: {
    icon?: React.ComponentType | string;
    linkStyles?: LinkPresentationProps;
    typographyStyles?: TypographyPresentationProps;
    iconStyles?: IconPresentationProps;
    iconPosition?: "left" | "right";
}) => {
    return {
        ...mergeWithCss(
            {
                mergeCss: true,
                typographyProps: {
                    size: 12,
                    lineHeight: "13px",
                    weight: fontConfig.weight.bold,
                    css: css`
                        letter-spacing: 0;
                    `,
                    ...typographyStyles,
                },
                icon: {
                    iconProps: {
                        src: icon,
                        size: 12,
                        ...iconStyles,
                    },
                    position: linkStyles?.icon?.position || iconPosition,
                },
            },
            linkStyles,
        ),
    } as LinkPresentationProps;
};

export const StyledHr = getStyledWrapper("hr");

export const HideEmptyWrapper = css`
    &:has(${DivWrapper}:empty) {
        display: none;
    }
`;

interface MoreActionsSharedStyle {
    overflowMenu?: OverflowMenuPresentationProps;
    moreActionsLink?: LinkPresentationProps;
}

export const moreActionsSharedStyle: MoreActionsSharedStyle = {
    overflowMenu: {
        _width: 140,
        cssOverrides: {
            wrapper: css`
                width: auto;
                height: auto;
                & button[aria-expanded="true"] ${IconWrapper} svg {
                    transform: rotate(180deg);
                }
            `,
            menu: css`
                padding: 4px 0;
                li:last-child button {
                    padding-bottom: 8px;
                    &::after {
                        display: none;
                    }
                }
            `,

            menuItem: css`
                padding: 8px 12px 0;
                font-size: 12px;
                line-height: 17px;
                color: ${color("text.link")};
                font-weight: bold;
                &:not([disabled]):hover,
                &:not([disabled]):focus {
                    color: ${color("common.ui8")};
                    outline: none;
                }
                &[disabled] {
                    opacity: 0.44;
                    cursor: not-allowed;
                    color: ${color("text.link")};
                }
                &::after {
                    content: "";
                    display: block;
                    border-bottom: 1px solid ${color("common.border")};
                    width: 100%;
                    margin-top: 8px;
                }
            `,
        },
    },
    moreActionsLink: {
        icon: {
            iconProps: {
                src: ChevronDown,
                size: 14,
            },
            position: "right",
        },
    },
};
